import { type Freshness, formatMinutesAgoLabel, minutesAgo } from "../lib/frames";
import { formatTimeForMode, formatOppositeZoneTooltip, type TimezoneMode } from "../lib/formatting";

/**
 * Freshness status chip: dot + time (local/UTC per §A4) + relative age
 * (DESIGN.md §6, §9). Aneks M002 §A5 adds a transient "aktualizacja…"
 * micro-state while a poll is verifying a new frame.
 */
export class StatusChip {
  readonly element: HTMLElement;
  private readonly dot: HTMLSpanElement;
  private readonly textEl: HTMLSpanElement;
  private readonly subEl: HTMLSpanElement;
  private timezoneMode: TimezoneMode = "local";
  private lastFrameMs: number | null = null;
  private lastFreshness: Freshness | "loading" = "loading";
  /** Active region/source name (M003) — was hardcoded "Radar USA"; region switch calls setDisplayName(). */
  private displayName: string;

  constructor(initialDisplayName = "Radar USA") {
    this.displayName = initialDisplayName;
    this.element = document.createElement("div");
    this.element.className = "status-chip card";
    this.element.setAttribute("role", "status");
    this.element.dataset.freshness = "loading";
    this.element.dataset.updating = "false";

    this.dot = document.createElement("span");
    this.dot.className = "status-chip__dot";
    this.dot.setAttribute("aria-hidden", "true");

    this.textEl = document.createElement("span");
    this.textEl.className = "status-chip__text tabular-nums";
    this.textEl.textContent = `${this.displayName} · ładowanie…`;

    this.subEl = document.createElement("span");
    this.subEl.className = "status-chip__sub tabular-nums";

    this.element.append(this.dot, this.textEl, this.subEl);
  }

  setLoading(): void {
    this.element.dataset.freshness = "loading";
    this.element.dataset.updating = "false";
    this.lastFrameMs = null;
    this.lastFreshness = "loading";
    this.textEl.textContent = `${this.displayName} · ładowanie danych…`;
    this.subEl.textContent = "";
    this.element.title = "";
  }

  update(latestFrameMs: number, freshness: Freshness, nowMs: number): void {
    this.element.dataset.freshness = freshness;
    this.element.dataset.updating = "false";
    this.lastFrameMs = latestFrameMs;
    this.lastFreshness = freshness;
    const time = formatTimeForMode(latestFrameMs, this.timezoneMode);
    const mins = minutesAgo(latestFrameMs, nowMs);
    const label = formatMinutesAgoLabel(mins);
    this.textEl.textContent = `${this.displayName} · ${time}`;
    this.subEl.textContent = `· ${label}`;
    this.element.title = `Ostatnia obserwacja: ${formatOppositeZoneTooltip(latestFrameMs, this.timezoneMode)}`;
  }

  /** Switch the active region/source name (M003 region switch, DESIGN Aneks M003 §R2.3 "atomowa podmiana tożsamości"). Safe to call before setLoading()/update(). */
  setDisplayName(name: string): void {
    this.displayName = name;
  }

  /** Switch the displayed time between local and UTC (§A4) — re-renders the last known state. */
  setTimezoneMode(mode: TimezoneMode): void {
    this.timezoneMode = mode;
    if (this.lastFrameMs !== null) {
      const time = formatTimeForMode(this.lastFrameMs, this.timezoneMode);
      this.textEl.textContent = `${this.displayName} · ${time}`;
      this.element.title = `Ostatnia obserwacja: ${formatOppositeZoneTooltip(this.lastFrameMs, this.timezoneMode)}`;
    }
  }

  /**
   * Transient "aktualizacja…" state while a poll is fetching/verifying a new
   * frame (§A5). `update()`/`setLoading()` always clear it again, so callers
   * only need to set it to `true` right before the network call.
   */
  setUpdating(active: boolean): void {
    this.element.dataset.updating = active ? "true" : "false";
    if (active) {
      this.subEl.textContent = "· aktualizacja…";
    } else if (this.lastFrameMs !== null) {
      // Restore the real relative label rather than leaving a stale "…" —
      // callers normally follow with update()/setLoading() immediately, but
      // this keeps the chip honest if that call is ever skipped.
      const mins = minutesAgo(this.lastFrameMs, Date.now());
      this.subEl.textContent = `· ${formatMinutesAgoLabel(mins)}`;
    }
  }

  getFreshness(): Freshness | "loading" {
    return this.lastFreshness;
  }
}
